import { useAutocomplete, type WithOptional } from 'admin';
import { type Port, PortTypes } from 'api';

import { AutocompleteField, type AutocompleteFieldProps } from '../AutocompleteField/AutocompleteField';
import { getCountryNameByCountryAbbr } from '../CountryAutocompleteField/countries';

type Props = Omit<WithOptional<AutocompleteFieldProps<Port>, 'label'>, 'options' | 'defaultValue'> & {
  defaultValue?: Port['id'];
  type?: PortTypes;
};

const getPortTypeDisplayedName = (portType: PortTypes | undefined) => {
  switch (portType) {
    case PortTypes.ICD:
      return 'ICD';
    case PortTypes.REG:
      return 'Port';
    default:
      return 'Port';
  }
};

export const getPortDisplayedName = (port: Port) =>
  `${getPortTypeDisplayedName(port.type)} ${port.name} in ${getCountryNameByCountryAbbr(port.country || 'GB')} [${port.unlocode}]`;

export const PortAutocomplete = ({
  name,
  label = 'Select port',
  required = false,
  disabled = false,
  type = undefined,
  defaultValue,
  ...rest
}: Props) => {
  const { autocompleteProps } = useAutocomplete<Port>({
    defaultValue,
    filters: type ? [{ field: 'type', operator: 'eq', value: type }] : [],
    onSearch: (value) => [
      {
        field: 'search',
        operator: 'contains',
        value,
      },
    ],
    resource: '/accounts/v1/ports/',
  });

  return (
    <AutocompleteField<Port>
      {...rest}
      {...autocompleteProps}
      name={name}
      label={label}
      valueField="id"
      required={required}
      disabled={disabled}
      getOptionLabel={(item) => getPortDisplayedName(item)}
    />
  );
};
